import { assertSafeInteger } from '@kenocyber/shared';

/**
 * `SHIP_BASE_DAMAGE_MILLI` — the one quantity every jewel is measured against.
 *
 * > **The damage, in milli-damage, of one ordinary projectile of the ship,
 * > before percentages, Primordial Hearts, runes, buffs, criticals and other
 * > jewels.**
 *
 * ## Why this module sits between the two modes rather than inside either
 *
 * The hunt and the duel derive it from different things - one from allocated
 * points and a component's flat base attack, the other from a normalised
 * profile - but they must derive the same MEANING, and the scaling into milli
 * has to happen in exactly one place. Putting it in `hunt/` would make the duel
 * import the hunt; putting it in `pvp/` would make the hunt import the duel.
 * Neither is acceptable, and a copy in each is the duplication this gate exists
 * to prevent.
 *
 * So this file knows nothing about either. It takes two plain numbers and
 * returns one. The two adapters that know where those numbers come from live in
 * their own domains - `pvpShipBaseDamageMilli` and `huntShipBaseDamageMilli` -
 * and both are one line over this function.
 *
 * ## What may never be an input here
 *
 * A percentage. A rune. A heart count. A critical. Another jewel. A
 * `projectile.damage`. There is no parameter any of them could be passed
 * through, which is stronger than a rule against passing them: the type
 * refuses.
 *
 * `projectile.damage` deserves naming twice, because it is the tempting one. It
 * is the FINAL damage of a shot - base times every summed percentage, plus the
 * bonus attack - decided when the shot was born. A jewel that read it would
 * scale with the artifacts, the equipment, the hearts and the rune that were
 * live at that instant, which is precisely the feedback the contract forbids.
 *
 * ## Integer, and exactly integer
 *
 * The result is a safe integer, always. The scaling asserts it rather than
 * rounding into it: a base that does not survive `x 1000` is a balance change
 * somebody made without noticing that damage stopped being expressible, and
 * `CLAUDE.md` is explicit that a malformed value is refused rather than
 * silently truncated.
 *
 * The ceiling is comfortable. The hunt's widest legitimate base is
 * `1 + MAX_RENDERED_POINTS` = 1 + 2^40, which scales to about 1,1 x 10^15 -
 * inside `Number.MAX_SAFE_INTEGER` (9,007 x 10^15) with room to spare. Anything
 * that would not fit throws instead of losing digits.
 */

/** 1 000 milli-damage is one point of damage. The unit, named once. */
export const DAMAGE_MILLI_PER_UNIT = 1000;

/**
 * The single formula.
 *
 * @param wholeBase     the ship's own base damage, in whole damage points,
 *                      before ANY percentage. The hunt's `effectiveBaseDamage`
 *                      or the duel's normalised `profile.baseDamage`.
 * @param flatBaseMilli a flat contribution that is already a BASE - never a
 *                      percentage, never a bonus applied after the multiplier.
 *                      The equipment's `baseAttackMilli` in the hunt; zero in
 *                      the duel, which normalises equipment away.
 */
export function shipBaseDamageMilli(wholeBase: number, flatBaseMilli: bigint): number {
  const scaled = wholeBase * DAMAGE_MILLI_PER_UNIT;
  /*
   * Asserted, not rounded. `1 x 1000` is exact and `1,5 x 1000` is exact, but
   * `0,1 x 1000` is 100,00000000000001 - and a base that arrives like that is a
   * balance change nobody proved was expressible, not a value to floor.
   */
  assertSafeInteger(scaled, 'ship base damage scaled to milli');
  const flat = Number(flatBaseMilli);
  assertSafeInteger(flat, 'flat base attack in milli');
  const total = scaled + flat;
  assertSafeInteger(total, 'ship base damage in milli');
  if (total < 0) {
    throw new RangeError(`ship base damage must not be negative, got ${total} milli`);
  }
  return total;
}

/**
 * The same quantity as an exact `bigint`, for arithmetic that must not round.
 *
 * The `number` above is already an exact integer; this is the explicit crossing
 * into the domain where a Primordial Heart count can be forty digits long. It
 * is a conversion a reader can see, which is the whole reason it is a function
 * rather than a `BigInt(...)` scattered at every call site.
 */
export function shipBaseDamageMilliExact(baseMilli: number): bigint {
  assertSafeInteger(baseMilli, 'ship base damage in milli');
  return BigInt(baseMilli);
}
